import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Modal, Form, Input, Select, Tag, Popconfirm, message, Space } from 'antd';
import { PlusOutlined, UserOutlined } from '@ant-design/icons';
import {
  AdminAccount,
  getAccountList,
  createAccount,
  updateAccountStatus,
  resetAccountPassword,
  updateAccountRole,
  getRoleList,
  AdminRole,
} from '../services/adminAccountApi';

const COLORS = {
  primary: '#1677ff',
  secondary: '#722ed1',
  success: '#52c41a',
  warning: '#faad14',
  error: '#ff4d4f',
};

const PageHeader: React.FC<{ title: string }> = ({ title }) => (
  <div style={{ marginBottom: 24 }}>
    <h2 style={{ margin: 0, fontSize: 20, fontWeight: 600 }}>
      <UserOutlined style={{ marginRight: 8, color: COLORS.primary }} />
      {title}
    </h2>
    <div style={{ width: 40, height: 3, background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`, borderRadius: 2, marginTop: 8 }} />
  </div>
);

const AccountManagement: React.FC = () => {
  const [accounts, setAccounts] = useState<AdminAccount[]>([]);
  const [roles, setRoles] = useState<AdminRole[]>([]);
  const [loading, setLoading] = useState(false);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [resetPasswordModalVisible, setResetPasswordModalVisible] = useState(false);
  const [resetPasswordId, setResetPasswordId] = useState<number | null>(null);
  const [createForm] = Form.useForm();
  const [resetForm] = Form.useForm();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [accountData, roleData] = await Promise.all([getAccountList(), getRoleList()]);
      setAccounts(accountData);
      setRoles(roleData);
    } catch (e: any) {
      message.error(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreate = async (values: { username: string; password: string; name: string; roleId?: number }) => {
    try {
      await createAccount(values);
      message.success('创建成功');
      setCreateModalVisible(false);
      createForm.resetFields();
      fetchData();
    } catch (e: any) {
      message.error(e.message);
    }
  };

  const handleToggleStatus = async (id: number, currentStatus: number) => {
    try {
      const newStatus = currentStatus === 1 ? 0 : 1;
      await updateAccountStatus(id, newStatus);
      message.success(newStatus === 1 ? '已启用' : '已禁用');
      fetchData();
    } catch (e: any) {
      message.error(e.message);
    }
  };

  const handleResetPassword = async (values: { password: string }) => {
    if (!resetPasswordId) return;
    try {
      await resetAccountPassword(resetPasswordId, values.password);
      message.success('密码重置成功');
      setResetPasswordModalVisible(false);
      resetForm.resetFields();
    } catch (e: any) {
      message.error(e.message);
    }
  };

  const handleRoleChange = async (id: number, roleId: number | undefined) => {
    try {
      await updateAccountRole(id, roleId ?? null);
      message.success('角色已更新');
      fetchData();
    } catch (e: any) {
      message.error(e.message);
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
    { title: '用户名', dataIndex: 'username', key: 'username', width: 120 },
    { title: '姓名', dataIndex: 'name', key: 'name', width: 120 },
    {
      title: '登录方式', dataIndex: 'loginType', key: 'loginType', width: 100,
      render: (type: string) => (
        <Tag color={type === 'lark' ? 'blue' : 'green'}>{type === 'lark' ? 'Lark' : '密码'}</Tag>
      ),
    },
    {
      title: '角色', dataIndex: 'roleId', key: 'roleId', width: 150,
      render: (_: any, record: AdminAccount) => (
        <Select
          style={{ width: 130 }}
          value={record.roleId || undefined}
          placeholder="未分配"
          allowClear
          onChange={(val) => handleRoleChange(record.id, val)}
          options={roles.filter(r => r.status === 1).map(r => ({ value: r.id, label: r.name }))}
          size="small"
        />
      ),
    },
    {
      title: '状态', dataIndex: 'status', key: 'status', width: 100,
      render: (status: number, record: AdminAccount) => (
        <Popconfirm
          title={status === 1 ? '确认禁用该账号？' : '确认启用该账号？'}
          onConfirm={() => handleToggleStatus(record.id, status)}
        >
          <Tag color={status === 1 ? 'success' : 'error'} style={{ cursor: 'pointer' }}>
            {status === 1 ? '启用' : '禁用'}
          </Tag>
        </Popconfirm>
      ),
    },
    { title: '最后登录', dataIndex: 'lastLoginTime', key: 'lastLoginTime', width: 170 },
    { title: '创建时间', dataIndex: 'createTime', key: 'createTime', width: 170 },
    {
      title: '操作', key: 'action', width: 100,
      render: (_: any, record: AdminAccount) => (
        <Space>
          {record.loginType === 'password' && (
            <Button
              type="link"
              size="small"
              onClick={() => {
                setResetPasswordId(record.id);
                setResetPasswordModalVisible(true);
              }}
            >
              重置密码
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <PageHeader title="账号管理" />
      <div style={{ marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateModalVisible(true)}>
          添加账号
        </Button>
      </div>
      <Table
        dataSource={accounts}
        columns={columns}
        rowKey="id"
        loading={loading}
        size="middle"
        pagination={{ pageSize: 20 }}
      />

      {/* 创建账号 Modal */}
      <Modal
        title="添加账号"
        open={createModalVisible}
        onCancel={() => { setCreateModalVisible(false); createForm.resetFields(); }}
        onOk={() => createForm.submit()}
        okText="创建"
      >
        <Form form={createForm} layout="vertical" onFinish={handleCreate}>
          <Form.Item name="username" label="用户名" rules={[{ required: true, message: '请输入用户名' }]}>
            <Input placeholder="请输入用户名" />
          </Form.Item>
          <Form.Item name="password" label="密码" rules={[
            { required: true, message: '请输入密码' },
            { min: 6, message: '密码不能少于6位' },
          ]}>
            <Input.Password placeholder="请输入密码（至少6位）" />
          </Form.Item>
          <Form.Item name="name" label="姓名">
            <Input placeholder="请输入姓名" />
          </Form.Item>
          <Form.Item name="roleId" label="角色">
            <Select
              placeholder="请选择角色"
              allowClear
              options={roles.filter(r => r.status === 1).map(r => ({ value: r.id, label: r.name }))}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* 重置密码 Modal */}
      <Modal
        title="重置密码"
        open={resetPasswordModalVisible}
        onCancel={() => { setResetPasswordModalVisible(false); resetForm.resetFields(); }}
        onOk={() => resetForm.submit()}
        okText="确认重置"
      >
        <Form form={resetForm} layout="vertical" onFinish={handleResetPassword}>
          <Form.Item name="password" label="新密码" rules={[
            { required: true, message: '请输入新密码' },
            { min: 6, message: '密码不能少于6位' },
          ]}>
            <Input.Password placeholder="请输入新密码（至少6位）" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AccountManagement;
